import { Head, router } from "@inertiajs/react"
import { useState } from "react"

import AppLayout from "@/layouts/app-layout"
import { formatDate as formatDateUtil } from "@/lib/date-utils"
import type { BreadcrumbItem } from "@/types"

/* ── Types ──────────────────────────────────────────────── */

interface CaseRecord {
  id: number
  case_number: string
  display_name: string
}

interface TimeEntry {
  id: number
  date: string | null
  user_name: string | null
  service_type: string | null
  is_tscm: boolean
  time_range: string | null
  actual_otj_minutes: number
  admin_minutes: number
  otj_minutes: number
  travel_minutes: number
  total_minutes: number
  hourly_rate: number
  time_cost: number
  mileage_to_job: number
  mileage_on_job: number
  mileage_from_job: number
  total_mileage: number
  mileage_rate: number
  mileage_cost: number
  flat_price: number
  total: number
  billed_at: string | null
  billed_by: string | null
  submitted: boolean
  approved: boolean
  status_label: string | null
}

interface Expense {
  id: number
  date: string | null
  user_name: string | null
  expense_type: string | null
  description: string | null
  amount: number
  billed_at: string | null
  billed_by: string | null
  submitted: boolean
  approved: boolean
  status_label: string | null
}

interface SummaryRow {
  user_name: string
  service_type: string
  actual_otj_minutes: number
  admin_minutes: number
  otj_minutes: number
  otj_total: number
  travel_minutes: number
  travel_total: number
  mileage: number
  mileage_to: number
  mileage_on: number
  mileage_from: number
  mileage_total: number
  grand_total: number
}

interface Totals {
  unbilled_time_total: number
  billed_time_total: number
  unbilled_time_only: number
  billed_time_only: number
  unbilled_mileage: number
  billed_mileage: number
  unbilled_tscm_total: number
  billed_tscm_total: number
  unbilled_expense_total: number
  billed_expense_total: number
}

interface Props {
  case_record: CaseRecord
  case_closed: boolean
  start_date: string | null
  end_date: string | null
  unbilled_time: TimeEntry[]
  billed_time: TimeEntry[]
  unbilled_tscm: TimeEntry[]
  billed_tscm: TimeEntry[]
  unbilled_expenses: Expense[]
  billed_expenses: Expense[]
  summary: SummaryRow[]
  totals: Totals
}

/* ── Helpers ────────────────────────────────────────────── */

function formatDate(dateStr: string | null): string {
  return formatDateUtil(dateStr, {
    month: "numeric",
    day: "numeric",
    year: "numeric",
  })
}

function formatMinutes(minutes: number): string {
  const h = Math.floor(minutes / 60)
  const m = minutes % 60
  return `${h}:${m.toString().padStart(2, "0")}`
}

function formatDollars(amount: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount)
}

function formatRate(rate: number): string {
  return `$${rate.toFixed(2)}`
}

const btnClass =
  "rounded bg-hpi-dark-blue px-2 py-0.5 text-[10px] font-medium text-white hover:bg-hpi-blue whitespace-nowrap"
const unbillClass =
  "text-hpi-red hover:text-red-800 text-[10px] whitespace-nowrap inline-flex items-center gap-0.5"

/* ── Section Components ─────────────────────────────────── */

function TimeTable({
  title,
  entries,
  total,
  isBilled,
  caseClosed,
  onMarkBilled,
  onMarkAllBilled,
  onUnbill,
}: {
  title: string
  entries: TimeEntry[]
  total: number
  isBilled: boolean
  caseClosed: boolean
  onMarkBilled?: (id: number) => void
  onMarkAllBilled?: () => void
  onUnbill?: (id: number) => void
}) {
  if (entries.length === 0) return null

  const showActions = !caseClosed

  return (
    <div className="mb-6">
      <div className="bg-hpi-dark-blue rounded-t px-4 py-2.5">
        <h3 className="text-sm font-semibold tracking-wider text-white uppercase">
          {title}
        </h3>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[1000px] border-collapse text-xs">
          <thead>
            <tr className="bg-hpi-pale-gray text-hpi-active-blue">
              <th className="border-hpi-light-gray border px-2 py-1.5 text-left">
                Date
              </th>
              <th className="border-hpi-light-gray border px-2 py-1.5 text-left">
                Investigator
              </th>
              <th className="border-hpi-light-gray border px-2 py-1.5 text-left">
                Type
              </th>
              <th className="border-hpi-light-gray border px-2 py-1.5 text-right">
                OTJ
              </th>
              <th className="border-hpi-light-gray border px-2 py-1.5 text-right">
                Block
              </th>
              <th className="border-hpi-light-gray border bg-yellow-50 px-2 py-1.5 text-right">
                Total OTJ
              </th>
              <th className="border-hpi-light-gray border px-2 py-1.5 text-right">
                Travel
              </th>
              <th className="border-hpi-light-gray border px-2 py-1.5 text-right">
                Total Time
              </th>
              <th className="border-hpi-light-gray border px-2 py-1.5 text-right">
                Rate
              </th>
              <th className="border-hpi-light-gray border px-2 py-1.5 text-right">
                To Job
              </th>
              <th className="border-hpi-light-gray border px-2 py-1.5 text-right">
                OTJ Mi
              </th>
              <th className="border-hpi-light-gray border px-2 py-1.5 text-right">
                From Job
              </th>
              <th className="border-hpi-light-gray border px-2 py-1.5 text-right">
                Total Mi
              </th>
              <th className="border-hpi-light-gray border px-2 py-1.5 text-right">
                Mi Rate
              </th>
              <th className="border-hpi-light-gray border px-2 py-1.5 text-right font-semibold">
                Total
              </th>
              <th className="border-hpi-light-gray border px-2 py-1.5 text-center">
                {isBilled ? "Billed" : ""}
              </th>
            </tr>
          </thead>
          <tbody>
            {entries.map((entry) => (
              <tr
                key={entry.id}
                className={`hover:bg-hpi-pale-blue ${
                  entry.status_label === "Not Submitted"
                    ? "bg-red-50"
                    : entry.status_label === "Not Approved"
                      ? "bg-orange-50"
                      : "bg-white"
                }`}
              >
                <td className="border-hpi-light-gray border px-2 py-1.5 whitespace-nowrap">
                  {formatDate(entry.date)}
                </td>
                <td className="border-hpi-light-gray border px-2 py-1.5">
                  {entry.user_name}
                </td>
                <td className="border-hpi-light-gray border px-2 py-1.5">
                  {entry.service_type}
                </td>
                <td className="border-hpi-light-gray border px-2 py-1.5 whitespace-nowrap">
                  {entry.time_range ?? ""}
                </td>
                <td className="border-hpi-light-gray border px-2 py-1.5 text-right">
                  {entry.admin_minutes > 0
                    ? formatMinutes(entry.admin_minutes)
                    : ""}
                </td>
                <td className="border-hpi-light-gray border bg-yellow-50 px-2 py-1.5 text-right font-medium">
                  {formatMinutes(entry.otj_minutes)}
                </td>
                <td className="border-hpi-light-gray border px-2 py-1.5 text-right">
                  {formatMinutes(entry.travel_minutes)}
                </td>
                <td className="border-hpi-light-gray border px-2 py-1.5 text-right font-medium">
                  {formatMinutes(entry.total_minutes)}
                </td>
                <td className="border-hpi-light-gray border px-2 py-1.5 text-right">
                  {formatRate(entry.hourly_rate)}
                </td>
                <td className="border-hpi-light-gray border px-2 py-1.5 text-right">
                  {entry.mileage_to_job || ""}
                </td>
                <td className="border-hpi-light-gray border px-2 py-1.5 text-right">
                  {entry.mileage_on_job || ""}
                </td>
                <td className="border-hpi-light-gray border px-2 py-1.5 text-right">
                  {entry.mileage_from_job || ""}
                </td>
                <td className="border-hpi-light-gray border px-2 py-1.5 text-right font-medium">
                  {entry.total_mileage || ""}
                </td>
                <td className="border-hpi-light-gray border px-2 py-1.5 text-right">
                  {entry.total_mileage > 0
                    ? formatRate(entry.mileage_rate)
                    : ""}
                </td>
                <td className="border-hpi-light-gray border px-2 py-1.5 text-right font-semibold">
                  {formatDollars(entry.total)}
                </td>
                <td className="border-hpi-light-gray border px-2 py-1.5 text-center whitespace-nowrap">
                  {isBilled && entry.billed_at ? (
                    <>
                      {showActions && onUnbill ? (
                        <button
                          className={unbillClass}
                          onClick={() => onUnbill(entry.id)}
                        >
                          {formatDate(entry.billed_at)} ✕
                        </button>
                      ) : (
                        <span className="text-hpi-dark-green text-xs">
                          {formatDate(entry.billed_at)}
                        </span>
                      )}
                      {entry.billed_by && (
                        <div className="text-hpi-med-gray text-[10px]">
                          {entry.billed_by}
                        </div>
                      )}
                    </>
                  ) : !isBilled && showActions && onMarkBilled ? (
                    <>
                      {entry.status_label && (
                        <span
                          className={`mr-1 text-[10px] font-medium ${
                            entry.status_label === "Not Submitted"
                              ? "text-hpi-red"
                              : "text-orange-600"
                          }`}
                        >
                          {entry.status_label}
                        </span>
                      )}
                      <button
                        className={btnClass}
                        onClick={() => onMarkBilled(entry.id)}
                      >
                        Mark Billed
                      </button>
                    </>
                  ) : entry.status_label ? (
                    <span
                      className={`text-xs font-medium ${
                        entry.status_label === "Not Submitted"
                          ? "text-hpi-red"
                          : "text-orange-600"
                      }`}
                    >
                      {entry.status_label}
                    </span>
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="bg-hpi-pale-gray font-semibold">
              <td
                colSpan={3}
                className="border-hpi-light-gray border px-2 py-1.5 text-right"
              >
                {isBilled ? "Total Billed:" : "Total Unbilled:"}
              </td>
              <td className="border-hpi-light-gray border px-2 py-1.5" />
              <td className="border-hpi-light-gray border px-2 py-1.5 text-right">
                {formatMinutes(
                  entries.reduce((s, e) => s + e.admin_minutes, 0),
                )}
              </td>
              <td className="border-hpi-light-gray border bg-yellow-50 px-2 py-1.5 text-right">
                {formatMinutes(entries.reduce((s, e) => s + e.otj_minutes, 0))}
              </td>
              <td className="border-hpi-light-gray border px-2 py-1.5 text-right">
                {formatMinutes(
                  entries.reduce((s, e) => s + e.travel_minutes, 0),
                )}
              </td>
              <td className="border-hpi-light-gray border px-2 py-1.5 text-right">
                {formatMinutes(
                  entries.reduce((s, e) => s + e.total_minutes, 0),
                )}
              </td>
              <td className="border-hpi-light-gray border px-2 py-1.5" />
              <td className="border-hpi-light-gray border px-2 py-1.5 text-right">
                {(() => {
                  const v = entries.reduce((s, e) => s + e.mileage_to_job, 0)
                  return v > 0 ? v : ""
                })()}
              </td>
              <td className="border-hpi-light-gray border px-2 py-1.5 text-right">
                {(() => {
                  const v = entries.reduce((s, e) => s + e.mileage_on_job, 0)
                  return v > 0 ? v : ""
                })()}
              </td>
              <td className="border-hpi-light-gray border px-2 py-1.5 text-right">
                {(() => {
                  const v = entries.reduce((s, e) => s + e.mileage_from_job, 0)
                  return v > 0 ? v : ""
                })()}
              </td>
              <td className="border-hpi-light-gray border px-2 py-1.5 text-right">
                {(() => {
                  const v = entries.reduce((s, e) => s + e.total_mileage, 0)
                  return v > 0 ? v : ""
                })()}
              </td>
              <td className="border-hpi-light-gray border px-2 py-1.5" />
              <td className="border-hpi-light-gray border px-2 py-1.5 text-right">
                {formatDollars(total)}
              </td>
              <td className="border-hpi-light-gray border px-2 py-1.5 text-center">
                {!isBilled && showActions && onMarkAllBilled && (
                  <button className={btnClass} onClick={onMarkAllBilled}>
                    Mark All Billed
                  </button>
                )}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  )
}

function TscmTable({
  title,
  entries,
  total,
  isBilled,
  caseClosed,
  onMarkBilled,
  onMarkAllBilled,
  onUnbill,
}: {
  title: string
  entries: TimeEntry[]
  total: number
  isBilled: boolean
  caseClosed: boolean
  onMarkBilled?: (id: number) => void
  onMarkAllBilled?: () => void
  onUnbill?: (id: number) => void
}) {
  if (entries.length === 0) return null

  const showActions = !caseClosed

  return (
    <div className="mb-6">
      <div className="bg-hpi-dark-blue rounded-t px-4 py-2.5">
        <h3 className="text-sm font-semibold tracking-wider text-white uppercase">
          {title}
        </h3>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full border-collapse text-xs">
          <thead>
            <tr className="bg-hpi-pale-gray text-hpi-active-blue">
              <th className="border-hpi-light-gray border px-2 py-1.5 text-left">
                Date
              </th>
              <th className="border-hpi-light-gray border px-2 py-1.5 text-left">
                Investigator
              </th>
              <th className="border-hpi-light-gray border px-2 py-1.5 text-left">
                Type
              </th>
              <th className="border-hpi-light-gray border px-2 py-1.5 text-right">
                Amount
              </th>
              <th className="border-hpi-light-gray border px-2 py-1.5 text-center">
                {isBilled ? "Billed" : ""}
              </th>
            </tr>
          </thead>
          <tbody>
            {entries.map((entry) => (
              <tr key={entry.id} className="hover:bg-hpi-pale-blue bg-white">
                <td className="border-hpi-light-gray border px-2 py-1.5 whitespace-nowrap">
                  {formatDate(entry.date)}
                </td>
                <td className="border-hpi-light-gray border px-2 py-1.5">
                  {entry.user_name}
                </td>
                <td className="border-hpi-light-gray border px-2 py-1.5">
                  {entry.service_type}
                </td>
                <td className="border-hpi-light-gray border px-2 py-1.5 text-right font-semibold">
                  {entry.flat_price > 0 ? formatDollars(entry.flat_price) : ""}
                </td>
                <td className="border-hpi-light-gray border px-2 py-1.5 text-center whitespace-nowrap">
                  {isBilled && entry.billed_at ? (
                    <>
                      {showActions && onUnbill ? (
                        <button
                          className={unbillClass}
                          onClick={() => onUnbill(entry.id)}
                        >
                          {formatDate(entry.billed_at)} ✕
                        </button>
                      ) : (
                        <span className="text-hpi-dark-green text-xs">
                          {formatDate(entry.billed_at)}
                        </span>
                      )}
                      {entry.billed_by && (
                        <div className="text-hpi-med-gray text-[10px]">
                          {entry.billed_by}
                        </div>
                      )}
                    </>
                  ) : !isBilled && showActions && onMarkBilled ? (
                    <>
                      {entry.status_label && (
                        <span
                          className={`mr-1 text-[10px] font-medium ${
                            entry.status_label === "Not Submitted"
                              ? "text-hpi-red"
                              : "text-orange-600"
                          }`}
                        >
                          {entry.status_label}
                        </span>
                      )}
                      <button
                        className={btnClass}
                        onClick={() => onMarkBilled(entry.id)}
                      >
                        Mark Billed
                      </button>
                    </>
                  ) : entry.status_label ? (
                    <span
                      className={`text-xs font-medium ${
                        entry.status_label === "Not Submitted"
                          ? "text-hpi-red"
                          : "text-orange-600"
                      }`}
                    >
                      {entry.status_label}
                    </span>
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="bg-hpi-pale-gray font-semibold">
              <td
                colSpan={3}
                className="border-hpi-light-gray border px-2 py-1.5 text-right"
              >
                {isBilled ? "Total Billed:" : "Total Unbilled:"}
              </td>
              <td className="border-hpi-light-gray border px-2 py-1.5 text-right">
                {formatDollars(total)}
              </td>
              <td className="border-hpi-light-gray border px-2 py-1.5 text-center">
                {!isBilled && showActions && onMarkAllBilled && (
                  <button className={btnClass} onClick={onMarkAllBilled}>
                    Mark All Billed
                  </button>
                )}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  )
}

function ExpenseTable({
  title,
  expenses,
  total,
  isBilled,
  caseClosed,
  onMarkBilled,
  onMarkAllBilled,
  onUnbill,
}: {
  title: string
  expenses: Expense[]
  total: number
  isBilled: boolean
  caseClosed: boolean
  onMarkBilled?: (id: number) => void
  onMarkAllBilled?: () => void
  onUnbill?: (id: number) => void
}) {
  if (expenses.length === 0) return null

  const showActions = !caseClosed

  return (
    <div className="mb-6">
      <div className="bg-hpi-dark-blue rounded-t px-4 py-2.5">
        <h3 className="text-sm font-semibold tracking-wider text-white uppercase">
          {title}
        </h3>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full border-collapse text-xs">
          <thead>
            <tr className="bg-hpi-pale-gray text-hpi-active-blue">
              <th className="border-hpi-light-gray border px-2 py-1.5 text-left">
                Date
              </th>
              <th className="border-hpi-light-gray border px-2 py-1.5 text-left">
                Investigator - Payee (Type)
              </th>
              <th className="border-hpi-light-gray border px-2 py-1.5 text-right">
                Total
              </th>
              <th className="border-hpi-light-gray border px-2 py-1.5 text-center">
                {isBilled ? "Billed" : ""}
              </th>
            </tr>
          </thead>
          <tbody>
            {expenses.map((expense) => (
              <tr
                key={expense.id}
                className={`hover:bg-hpi-pale-blue ${
                  expense.status_label === "Not Submitted"
                    ? "bg-red-50"
                    : expense.status_label === "Not Approved"
                      ? "bg-orange-50"
                      : "bg-white"
                }`}
              >
                <td className="border-hpi-light-gray border px-2 py-1.5 whitespace-nowrap">
                  {formatDate(expense.date)}
                </td>
                <td className="border-hpi-light-gray border px-2 py-1.5">
                  {expense.user_name}
                  {expense.expense_type && ` (${expense.expense_type})`}
                  {expense.description && (
                    <div className="text-hpi-med-gray mt-0.5 text-[10px]">
                      {expense.description}
                    </div>
                  )}
                </td>
                <td className="border-hpi-light-gray border px-2 py-1.5 text-right font-semibold">
                  {formatDollars(expense.amount)}
                </td>
                <td className="border-hpi-light-gray border px-2 py-1.5 text-center whitespace-nowrap">
                  {isBilled && expense.billed_at ? (
                    <>
                      {showActions && onUnbill ? (
                        <button
                          className={unbillClass}
                          onClick={() => onUnbill(expense.id)}
                        >
                          {formatDate(expense.billed_at)} ✕
                        </button>
                      ) : (
                        <span className="text-hpi-dark-green text-xs">
                          {formatDate(expense.billed_at)}
                        </span>
                      )}
                      {expense.billed_by && (
                        <div className="text-hpi-med-gray text-[10px]">
                          {expense.billed_by}
                        </div>
                      )}
                    </>
                  ) : !isBilled && showActions && onMarkBilled ? (
                    <>
                      {expense.status_label && (
                        <span
                          className={`mr-1 text-[10px] font-medium ${
                            expense.status_label === "Not Submitted"
                              ? "text-hpi-red"
                              : "text-orange-600"
                          }`}
                        >
                          {expense.status_label}
                        </span>
                      )}
                      <button
                        className={btnClass}
                        onClick={() => onMarkBilled(expense.id)}
                      >
                        Mark Billed
                      </button>
                    </>
                  ) : expense.status_label ? (
                    <span
                      className={`text-xs font-medium ${
                        expense.status_label === "Not Submitted"
                          ? "text-hpi-red"
                          : "text-orange-600"
                      }`}
                    >
                      {expense.status_label}
                    </span>
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="bg-hpi-pale-gray font-semibold">
              <td
                colSpan={2}
                className="border-hpi-light-gray border px-2 py-1.5 text-right"
              >
                {isBilled ? "Total Billed:" : "Total Unbilled:"}
              </td>
              <td className="border-hpi-light-gray border px-2 py-1.5 text-right">
                {formatDollars(total)}
              </td>
              <td className="border-hpi-light-gray border px-2 py-1.5 text-center">
                {!isBilled && showActions && onMarkAllBilled && (
                  <button className={btnClass} onClick={onMarkAllBilled}>
                    Mark All Billed
                  </button>
                )}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  )
}

function SummaryTable({ summary }: { summary: SummaryRow[] }) {
  if (summary.length === 0) return null

  const grandTotal = summary.reduce((sum, row) => sum + row.grand_total, 0)

  return (
    <div className="mb-6">
      <div className="bg-hpi-dark-blue rounded-t px-4 py-2.5">
        <h3 className="text-sm font-semibold tracking-wider text-white uppercase">
          Summary by Employee / Service Type
        </h3>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[900px] border-collapse text-xs">
          <thead>
            <tr className="bg-hpi-pale-gray text-hpi-active-blue">
              <th className="border-hpi-light-gray border px-2 py-1.5 text-left">
                Employee
              </th>
              <th className="border-hpi-light-gray border px-2 py-1.5 text-left">
                Service Type
              </th>
              <th className="border-hpi-light-gray border px-2 py-1.5 text-right">
                OTJ
              </th>
              <th className="border-hpi-light-gray border px-2 py-1.5 text-right">
                Block
              </th>
              <th className="border-hpi-light-gray border bg-yellow-50 px-2 py-1.5 text-right">
                Total OTJ
              </th>
              <th className="border-hpi-light-gray border px-2 py-1.5 text-right">
                OTJ $
              </th>
              <th className="border-hpi-light-gray border px-2 py-1.5 text-right">
                Travel
              </th>
              <th className="border-hpi-light-gray border px-2 py-1.5 text-right">
                Travel $
              </th>
              <th className="border-hpi-light-gray border px-2 py-1.5 text-right">
                Time Total
              </th>
              <th className="border-hpi-light-gray border px-2 py-1.5 text-right">
                To Job Mi
              </th>
              <th className="border-hpi-light-gray border px-2 py-1.5 text-right">
                OTJ Mi
              </th>
              <th className="border-hpi-light-gray border px-2 py-1.5 text-right">
                From Job Mi
              </th>
              <th className="border-hpi-light-gray border px-2 py-1.5 text-right">
                Total Mi
              </th>
              <th className="border-hpi-light-gray border px-2 py-1.5 text-right">
                Mileage $
              </th>
              <th className="border-hpi-light-gray border px-2 py-1.5 text-right font-semibold">
                Total
              </th>
            </tr>
          </thead>
          <tbody>
            {summary.map((row, i) => (
              <tr key={i} className="hover:bg-hpi-pale-blue bg-white">
                <td className="border-hpi-light-gray border px-2 py-1.5">
                  {row.user_name}
                </td>
                <td className="border-hpi-light-gray border px-2 py-1.5">
                  {row.service_type}
                </td>
                <td className="border-hpi-light-gray border px-2 py-1.5 text-right">
                  {row.actual_otj_minutes > 0
                    ? formatMinutes(row.actual_otj_minutes)
                    : ""}
                </td>
                <td className="border-hpi-light-gray border px-2 py-1.5 text-right">
                  {row.admin_minutes > 0
                    ? formatMinutes(row.admin_minutes)
                    : ""}
                </td>
                <td className="border-hpi-light-gray border bg-yellow-50 px-2 py-1.5 text-right">
                  {row.otj_minutes > 0 ? formatMinutes(row.otj_minutes) : ""}
                </td>
                <td className="border-hpi-light-gray border px-2 py-1.5 text-right">
                  {row.otj_total > 0 ? formatDollars(row.otj_total) : ""}
                </td>
                <td className="border-hpi-light-gray border px-2 py-1.5 text-right">
                  {row.travel_minutes > 0
                    ? formatMinutes(row.travel_minutes)
                    : ""}
                </td>
                <td className="border-hpi-light-gray border px-2 py-1.5 text-right">
                  {row.travel_total > 0 ? formatDollars(row.travel_total) : ""}
                </td>
                <td className="border-hpi-light-gray border px-2 py-1.5 text-right">
                  {row.otj_total > 0 || row.travel_total > 0
                    ? formatDollars(row.otj_total + row.travel_total)
                    : ""}
                </td>
                <td className="border-hpi-light-gray border px-2 py-1.5 text-right">
                  {row.mileage_to > 0 ? row.mileage_to : ""}
                </td>
                <td className="border-hpi-light-gray border px-2 py-1.5 text-right">
                  {row.mileage_on > 0 ? row.mileage_on : ""}
                </td>
                <td className="border-hpi-light-gray border px-2 py-1.5 text-right">
                  {row.mileage_from > 0 ? row.mileage_from : ""}
                </td>
                <td className="border-hpi-light-gray border px-2 py-1.5 text-right">
                  {row.mileage > 0 ? row.mileage : ""}
                </td>
                <td className="border-hpi-light-gray border px-2 py-1.5 text-right">
                  {row.mileage_total > 0
                    ? formatDollars(row.mileage_total)
                    : ""}
                </td>
                <td className="border-hpi-light-gray border px-2 py-1.5 text-right font-semibold">
                  {formatDollars(row.grand_total)}
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="border-hpi-light-gray border-t-4 border-t-gray-800 font-semibold">
              <td
                colSpan={2}
                className="border-hpi-light-gray border px-2 py-1.5 text-right"
              >
                Total:
              </td>
              <td className="border-hpi-light-gray border px-2 py-1.5 text-right">
                {(() => {
                  const v = summary.reduce(
                    (s, r) => s + r.actual_otj_minutes,
                    0,
                  )
                  return v > 0 ? formatMinutes(v) : ""
                })()}
              </td>
              <td className="border-hpi-light-gray border px-2 py-1.5 text-right">
                {(() => {
                  const v = summary.reduce((s, r) => s + r.admin_minutes, 0)
                  return v > 0 ? formatMinutes(v) : ""
                })()}
              </td>
              <td className="border-hpi-light-gray border bg-yellow-50 px-2 py-1.5 text-right">
                {(() => {
                  const v = summary.reduce((s, r) => s + r.otj_minutes, 0)
                  return v > 0 ? formatMinutes(v) : ""
                })()}
              </td>
              <td className="border-hpi-light-gray border px-2 py-1.5 text-right">
                {(() => {
                  const v = summary.reduce((s, r) => s + r.otj_total, 0)
                  return v > 0 ? formatDollars(v) : ""
                })()}
              </td>
              <td className="border-hpi-light-gray border px-2 py-1.5 text-right">
                {(() => {
                  const v = summary.reduce((s, r) => s + r.travel_minutes, 0)
                  return v > 0 ? formatMinutes(v) : ""
                })()}
              </td>
              <td className="border-hpi-light-gray border px-2 py-1.5 text-right">
                {(() => {
                  const v = summary.reduce((s, r) => s + r.travel_total, 0)
                  return v > 0 ? formatDollars(v) : ""
                })()}
              </td>
              <td className="border-hpi-light-gray border px-2 py-1.5 text-right">
                {(() => {
                  const otj = summary.reduce((s, r) => s + r.otj_total, 0)
                  const travel = summary.reduce((s, r) => s + r.travel_total, 0)
                  return otj > 0 || travel > 0
                    ? formatDollars(otj + travel)
                    : ""
                })()}
              </td>
              <td className="border-hpi-light-gray border px-2 py-1.5 text-right">
                {(() => {
                  const v = summary.reduce((s, r) => s + r.mileage_to, 0)
                  return v > 0 ? v : ""
                })()}
              </td>
              <td className="border-hpi-light-gray border px-2 py-1.5 text-right">
                {(() => {
                  const v = summary.reduce((s, r) => s + r.mileage_on, 0)
                  return v > 0 ? v : ""
                })()}
              </td>
              <td className="border-hpi-light-gray border px-2 py-1.5 text-right">
                {(() => {
                  const v = summary.reduce((s, r) => s + r.mileage_from, 0)
                  return v > 0 ? v : ""
                })()}
              </td>
              <td className="border-hpi-light-gray border px-2 py-1.5 text-right">
                {(() => {
                  const v = summary.reduce((s, r) => s + r.mileage, 0)
                  return v > 0 ? v : ""
                })()}
              </td>
              <td className="border-hpi-light-gray border px-2 py-1.5 text-right">
                {(() => {
                  const v = summary.reduce((s, r) => s + r.mileage_total, 0)
                  return v > 0 ? formatDollars(v) : ""
                })()}
              </td>
              <td className="border-hpi-light-gray border px-2 py-1.5 text-right">
                {formatDollars(grandTotal)}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  )
}

function GrandSummaryTable({ totals }: { totals: Totals }) {
  const billedTimeOnly = totals.billed_time_only + totals.billed_tscm_total
  const unbilledTimeOnly =
    totals.unbilled_time_only + totals.unbilled_tscm_total
  const billedMileage = totals.billed_mileage
  const unbilledMileage = totals.unbilled_mileage
  const billedExpenses = totals.billed_expense_total
  const unbilledExpenses = totals.unbilled_expense_total

  const billedTotal = billedTimeOnly + billedMileage + billedExpenses
  const unbilledTotal = unbilledTimeOnly + unbilledMileage + unbilledExpenses
  const grandTotal = billedTotal + unbilledTotal

  if (grandTotal === 0) return null

  return (
    <div className="mb-6">
      <div className="overflow-x-auto">
        <table className="w-full max-w-lg border-collapse text-xs">
          <thead>
            <tr className="bg-hpi-pale-gray text-hpi-active-blue">
              <th className="border-hpi-light-gray border px-3 py-1.5 text-left" />
              <th className="border-hpi-light-gray border px-3 py-1.5 text-right">
                Billed
              </th>
              <th className="border-hpi-light-gray border px-3 py-1.5 text-right">
                Unbilled
              </th>
              <th className="border-hpi-light-gray border px-3 py-1.5 text-right">
                Total
              </th>
            </tr>
          </thead>
          <tbody>
            <tr className="bg-white">
              <td className="border-hpi-light-gray border px-3 py-1.5 font-semibold">
                Time
              </td>
              <td className="border-hpi-light-gray border px-3 py-1.5 text-right">
                {billedTimeOnly > 0 ? formatDollars(billedTimeOnly) : ""}
              </td>
              <td className="border-hpi-light-gray border px-3 py-1.5 text-right">
                {unbilledTimeOnly > 0 ? formatDollars(unbilledTimeOnly) : ""}
              </td>
              <td className="border-hpi-light-gray border px-3 py-1.5 text-right">
                {billedTimeOnly > 0 || unbilledTimeOnly > 0
                  ? formatDollars(billedTimeOnly + unbilledTimeOnly)
                  : ""}
              </td>
            </tr>
            <tr className="bg-white">
              <td className="border-hpi-light-gray border px-3 py-1.5 font-semibold">
                Mileage
              </td>
              <td className="border-hpi-light-gray border px-3 py-1.5 text-right">
                {billedMileage > 0 ? formatDollars(billedMileage) : ""}
              </td>
              <td className="border-hpi-light-gray border px-3 py-1.5 text-right">
                {unbilledMileage > 0 ? formatDollars(unbilledMileage) : ""}
              </td>
              <td className="border-hpi-light-gray border px-3 py-1.5 text-right">
                {billedMileage > 0 || unbilledMileage > 0
                  ? formatDollars(billedMileage + unbilledMileage)
                  : ""}
              </td>
            </tr>
            <tr className="bg-white">
              <td className="border-hpi-light-gray border px-3 py-1.5 font-semibold">
                Expenses
              </td>
              <td className="border-hpi-light-gray border px-3 py-1.5 text-right">
                {billedExpenses > 0 ? formatDollars(billedExpenses) : ""}
              </td>
              <td className="border-hpi-light-gray border px-3 py-1.5 text-right">
                {unbilledExpenses > 0 ? formatDollars(unbilledExpenses) : ""}
              </td>
              <td className="border-hpi-light-gray border px-3 py-1.5 text-right">
                {billedExpenses > 0 || unbilledExpenses > 0
                  ? formatDollars(billedExpenses + unbilledExpenses)
                  : ""}
              </td>
            </tr>
          </tbody>
          <tfoot>
            <tr className="bg-amber-200 font-bold">
              <td className="border-hpi-light-gray border px-3 py-1.5">
                Total
              </td>
              <td className="border-hpi-light-gray border px-3 py-1.5 text-right">
                {billedTotal > 0 ? formatDollars(billedTotal) : ""}
              </td>
              <td className="border-hpi-light-gray border px-3 py-1.5 text-right">
                {unbilledTotal > 0 ? formatDollars(unbilledTotal) : ""}
              </td>
              <td className="border-hpi-light-gray border px-3 py-1.5 text-right">
                {grandTotal > 0 ? formatDollars(grandTotal) : ""}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  )
}

/* ── Main Component ────────────────────────────────────── */

export default function BillingDetailReport({
  case_record,
  case_closed,
  start_date,
  end_date,
  unbilled_time,
  billed_time,
  unbilled_tscm,
  billed_tscm,
  unbilled_expenses,
  billed_expenses,
  summary,
  totals,
}: Props) {
  const breadcrumbs: BreadcrumbItem[] = [
    { title: "Cases", href: "/cases" },
    {
      title: case_record.display_name || case_record.case_number,
      href: `/cases/${case_record.id}`,
    },
    {
      title: "Billing Detail",
      href: `/cases/${case_record.id}/billing_reports/detail`,
    },
  ]

  const [startDate, setStartDate] = useState(start_date ?? "")
  const [endDate, setEndDate] = useState(end_date ?? "")

  const basePath = `/cases/${case_record.id}/billing_reports`

  function filterQuery(): string {
    const p = new URLSearchParams()
    if (start_date) p.set("start_date", start_date)
    if (end_date) p.set("end_date", end_date)
    return p.toString()
  }

  function patchAction(
    action: string,
    extraParams: Record<string, string | number> = {},
  ) {
    const qs = filterQuery()
    router.patch(
      `${basePath}/${action}${qs ? `?${qs}` : ""}`,
      extraParams as Record<string, string>,
      { preserveScroll: true },
    )
  }

  const handleFilter = () => {
    const params = new URLSearchParams()
    if (startDate) params.set("start_date", startDate)
    if (endDate) params.set("end_date", endDate)
    const qs = params.toString()
    router.get(`${basePath}/detail${qs ? `?${qs}` : ""}`)
  }

  const handleClearFilter = () => {
    setStartDate("")
    setEndDate("")
    router.get(`${basePath}/detail`)
  }

  const overallTotal =
    totals.unbilled_time_total +
    totals.billed_time_total +
    totals.unbilled_tscm_total +
    totals.billed_tscm_total +
    totals.unbilled_expense_total +
    totals.billed_expense_total

  const overallUnbilled =
    totals.unbilled_time_total +
    totals.unbilled_tscm_total +
    totals.unbilled_expense_total

  const overallBilled =
    totals.billed_time_total +
    totals.billed_tscm_total +
    totals.billed_expense_total

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title={`Billing Detail - ${case_record.display_name}`} />

      <div className="p-6">
        <div className="mb-4 flex items-center justify-between">
          <h1 className="text-2xl font-semibold">
            Billing Detail — {case_record.display_name}
          </h1>
          {case_closed && (
            <span className="bg-hpi-red rounded px-3 py-1 text-sm font-medium text-white">
              Case Closed
            </span>
          )}
        </div>

        {/* Date filter */}
        <div className="border-hpi-light-gray bg-hpi-pale-gray mb-6 flex flex-wrap items-end gap-4 rounded border p-4">
          <div className="flex items-center gap-2">
            <label
              htmlFor="billing_start"
              className="text-hpi-med-gray text-sm"
            >
              Start Date
            </label>
            <input
              id="billing_start"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="border-hpi-light-gray rounded border px-2 py-1 text-sm"
            />
          </div>
          <div className="flex items-center gap-2">
            <label htmlFor="billing_end" className="text-hpi-med-gray text-sm">
              End Date
            </label>
            <input
              id="billing_end"
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="border-hpi-light-gray rounded border px-2 py-1 text-sm"
            />
          </div>
          <button
            onClick={handleFilter}
            className="bg-hpi-dark-blue hover:bg-hpi-blue rounded px-4 py-1 text-sm text-white"
          >
            Filter
          </button>
          {(startDate || endDate) && (
            <button
              onClick={handleClearFilter}
              className="text-hpi-med-gray hover:text-hpi-dark-gray text-sm underline"
            >
              Clear
            </button>
          )}
        </div>

        {/* Grand totals bar */}
        <div className="border-hpi-light-gray mb-6 grid grid-cols-3 gap-4 rounded border bg-white p-4">
          <div className="text-center">
            <div className="text-hpi-med-gray text-xs uppercase">Unbilled</div>
            <div className="text-lg font-bold text-orange-600">
              {formatDollars(overallUnbilled)}
            </div>
          </div>
          <div className="text-center">
            <div className="text-hpi-med-gray text-xs uppercase">Billed</div>
            <div className="text-hpi-dark-green text-lg font-bold">
              {formatDollars(overallBilled)}
            </div>
          </div>
          <div className="text-center">
            <div className="text-hpi-med-gray text-xs uppercase">
              Grand Total
            </div>
            <div className="text-hpi-dark-blue text-lg font-bold">
              {formatDollars(overallTotal)}
            </div>
          </div>
        </div>

        {/* Unbilled Time & Mileage */}
        <TimeTable
          title="Unbilled Time & Mileage"
          entries={unbilled_time}
          total={totals.unbilled_time_total}
          isBilled={false}
          caseClosed={case_closed}
          onMarkBilled={(id) => patchAction("mark_time_billed", { id })}
          onMarkAllBilled={() => patchAction("mark_all_time_billed")}
        />

        {/* Billed Time & Mileage */}
        <TimeTable
          title="Billed Time & Mileage"
          entries={billed_time}
          total={totals.billed_time_total}
          isBilled={true}
          caseClosed={case_closed}
          onUnbill={(id) => patchAction("unbill_time", { id })}
        />

        {/* Unbilled TSCM/Forensics */}
        <TscmTable
          title="Unbilled TSCM/Forensics"
          entries={unbilled_tscm}
          total={totals.unbilled_tscm_total}
          isBilled={false}
          caseClosed={case_closed}
          onMarkBilled={(id) => patchAction("mark_tscm_billed", { id })}
          onMarkAllBilled={() => patchAction("mark_all_tscm_billed")}
        />

        {/* Billed TSCM/Forensics */}
        <TscmTable
          title="Billed TSCM/Forensics"
          entries={billed_tscm}
          total={totals.billed_tscm_total}
          isBilled={true}
          caseClosed={case_closed}
          onUnbill={(id) => patchAction("unbill_time", { id })}
        />

        {/* Unbilled Expenses */}
        <ExpenseTable
          title="Unbilled Expenses"
          expenses={unbilled_expenses}
          total={totals.unbilled_expense_total}
          isBilled={false}
          caseClosed={case_closed}
          onMarkBilled={(id) => patchAction("mark_expense_billed", { id })}
          onMarkAllBilled={() => patchAction("mark_all_expenses_billed")}
        />

        {/* Billed Expenses */}
        <ExpenseTable
          title="Billed Expenses"
          expenses={billed_expenses}
          total={totals.billed_expense_total}
          isBilled={true}
          caseClosed={case_closed}
          onUnbill={(id) => patchAction("unbill_expense", { id })}
        />

        {/* Summary by Employee / Service Type */}
        <SummaryTable summary={summary} />

        {/* Grand Summary (Billed/Unbilled/Total) — matches legacy layout */}
        <GrandSummaryTable totals={totals} />

        <div className="mt-4">
          <a
            href={`/cases/${case_record.id}`}
            className="text-hpi-active-blue hover:underline"
          >
            Back to Case
          </a>
        </div>
      </div>
    </AppLayout>
  )
}
